// HTTP layer + device identity (TECH_SPEC §3.2, §4.4).

import type {
  BootData,
  NicknameResponse,
  Opponent,
  OpponentResponse,
  RivalResponse,
  SubmitRunBody,
  SubmitRunResponse,
  Track,
} from './types';
import { classifySubmitFailure, shouldKeepPending } from './submitOutcome';

const DEVICE_KEY = 'beatme.device';
const PLAYED_KEY = 'beatme.played';
const PENDING_RUN_KEY = 'beatme.pendingRun';
const PENDING_TRIES_KEY = 'beatme.pendingRun.tries';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

function safeLocalGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeLocalSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* private mode / storage disabled — degrade to a session-only identity */
  }
}

function readCookie(name: string): string | null {
  const parts = document.cookie ? document.cookie.split('; ') : [];
  for (const part of parts) {
    const eq = part.indexOf('=');
    if (eq > 0 && part.slice(0, eq) === name) return decodeURIComponent(part.slice(eq + 1));
  }
  return null;
}

function writeCookie(name: string, value: string): void {
  const oneYear = 60 * 60 * 24 * 365;
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${oneYear}; samesite=lax`;
}

function uuid(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  const bytes = new Uint8Array(16);
  if (c && typeof c.getRandomValues === 'function') c.getRandomValues(bytes);
  else for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex: string[] = [];
  for (let i = 0; i < 16; i++) hex.push((bytes[i] ?? 0).toString(16).padStart(2, '0'));
  return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex
    .slice(6, 8)
    .join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`;
}

let deviceId = '';

/** localStorage is the source of truth; the cookie mirror lets the share page
 *  render "your own run" server-side (design §5.1). */
export function ensureDeviceId(): string {
  if (deviceId) return deviceId;
  const stored = safeLocalGet(DEVICE_KEY) ?? readCookie(DEVICE_KEY);
  deviceId = stored ?? uuid();
  safeLocalSet(DEVICE_KEY, deviceId);
  writeCookie(DEVICE_KEY, deviceId);
  return deviceId;
}

export function hasPlayedBefore(): boolean {
  return safeLocalGet(PLAYED_KEY) === '1';
}

export function markPlayed(): void {
  safeLocalSet(PLAYED_KEY, '1');
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  headers.set('x-device-id', ensureDeviceId());
  if (init?.body !== undefined) headers.set('content-type', 'application/json');
  let res: Response;
  try {
    res = await fetch(path, { ...init, headers, credentials: 'same-origin' });
  } catch (err) {
    throw new ApiError(err instanceof Error ? err.message : 'network error', 0);
  }
  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const body: unknown = await res.json();
      if (body && typeof body === 'object' && 'error' in body) {
        message = String((body as { error: unknown }).error);
      }
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(message, res.status);
  }
  return (await res.json()) as T;
}

/**
 * §4bis.31: the SPA path must carry the link's VERB. In dev (and on any
 * `/r/:code` that reaches the client without inlined boot data) the page is
 * served by Vite and the app boots through this fetch — so dropping `?h=1`
 * here would silently turn every hand-back link into a challenge on exactly the
 * path the harness and the dev loop use.
 */
export function fetchBootByCode(code: string, verb?: 'challenge' | 'handoff'): Promise<BootData> {
  const q = verb === 'handoff' ? '?h=1' : '';
  return request<BootData>(`/api/boot/${encodeURIComponent(code)}${q}`);
}

export function fetchCurrentTrack(): Promise<Track> {
  return request<Track>('/api/track/current');
}

export async function fetchOpponent(
  trackId: string,
  entry: 'first' | 'walkin',
): Promise<Opponent | null> {
  const q = new URLSearchParams({ trackId, entry, deviceId: ensureDeviceId() });
  const res = await request<OpponentResponse>(`/api/opponent?${q.toString()}`);
  return res.opponent;
}

/**
 * Rival ladder rung 2 — 本周的你 (§4bis.21). The device's best run on this
 * track **including the one just played** (H02 B3): the moment a player most
 * wants to race their new record is the moment they just set it. Only ever
 * called by a device the ladder flag is on for.
 */
export async function fetchSelfBest(trackId: string): Promise<Opponent | null> {
  const q = new URLSearchParams({ trackId });
  const res = await request<RivalResponse>(`/api/self-best?${q.toString()}`);
  // `?? null` is load-bearing: the B4 cache rule reads `undefined` as "no
  // answer" (a thrown request) and `null` as "the server holds nothing of
  // ours". A 200 with a malformed body must land in the second bucket, not
  // silently in the first.
  return res.opponent ?? null;
}

/** Rival ladder rung 3 — 今日路人 (§4bis.21). Same stranger all UTC day. */
export async function fetchDailyRival(trackId: string): Promise<Opponent | null> {
  const q = new URLSearchParams({ trackId });
  const res = await request<RivalResponse>(`/api/daily-rival?${q.toString()}`);
  return res.opponent;
}

export function submitRun(body: SubmitRunBody): Promise<SubmitRunResponse> {
  return request<SubmitRunResponse>('/api/runs', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function setNickname(nickname: string): Promise<NicknameResponse> {
  return request<NicknameResponse>('/api/nickname', {
    method: 'POST',
    body: JSON.stringify({ nickname }),
  });
}

export function rerollNickname(): Promise<NicknameResponse> {
  return request<NicknameResponse>('/api/nickname', {
    method: 'POST',
    body: JSON.stringify({ reroll: true }),
  });
}

/** Design §4.5: a run that couldn't be submitted is cached and retried later.
 *  A freshly failed run starts its retry budget over. */
export function cachePendingRun(body: SubmitRunBody): void {
  safeLocalSet(PENDING_RUN_KEY, JSON.stringify(body));
  safeLocalSet(PENDING_TRIES_KEY, '0');
}

/** Attempts already spent on the cached run (absent/garbage = none). */
function pendingTries(): number {
  const n = Number.parseInt(safeLocalGet(PENDING_TRIES_KEY) ?? '', 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export function takePendingRun(): SubmitRunBody | null {
  const raw = safeLocalGet(PENDING_RUN_KEY);
  if (!raw) return null;
  try {
    localStorage.removeItem(PENDING_RUN_KEY);
  } catch {
    /* ignore */
  }
  try {
    return JSON.parse(raw) as SubmitRunBody;
  } catch {
    return null;
  }
}

/**
 * Best-effort resubmission of a run cached while offline or unsaved.
 *
 * A run the server *judged and refused* is dropped rather than re-cached —
 * replaying the same stream to the same verifier cannot produce a different
 * answer — and so is a run that has burned its retry budget
 * (`PENDING_RUN_MAX_TRIES`). Either way the slot is freed for a run that still
 * has a chance (src/submitOutcome.ts).
 */
export async function flushPendingRun(): Promise<void> {
  const tries = pendingTries();
  const pending = takePendingRun();
  if (!pending) return;
  try {
    await submitRun(pending);
  } catch (err) {
    const status = err instanceof ApiError ? err.status : -1;
    if (!shouldKeepPending(status, tries)) return;
    safeLocalSet(PENDING_RUN_KEY, JSON.stringify(pending));
    // Offline attempts do not spend the budget (submitOutcome.ts).
    const spent = classifySubmitFailure(status).consumesBudget ? tries + 1 : tries;
    safeLocalSet(PENDING_TRIES_KEY, String(spent));
  }
}
